import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CITY_ROUTES } from '../cityRoutes';
import { VEHICLES_OFF } from '../config';
import { useWorldStore, ZOOM } from '../state/worldStore';
import CarModel from './CarModel';
import SafeMount from './SafeMount';
import { useGLTF } from '@react-three/drei';
import { useCarFleet, FLEET_SHIFT } from '../services/carFleet';
import { IS_MOBILE } from '../config';

const GROUND_Y = 0.12;   // waylines just above the street

// A soft repeating "comet" texture — a bright falloff blob that, tiled and
// scrolled along the tube, reads as energy flowing down the route.
function flowTexture() {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 8;
  const g = c.getContext('2d');
  const grad = g.createLinearGradient(0, 0, 128, 0);
  grad.addColorStop(0.0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.5, 'rgba(255,255,255,0.15)');
  grad.addColorStop(0.82, 'rgba(255,255,255,1)');
  grad.addColorStop(0.92, 'rgba(255,255,255,1)');
  grad.addColorStop(1.0, 'rgba(255,255,255,0)');
  g.fillStyle = grad; g.fillRect(0, 0, 128, 8);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
}

function curveFor(points) {
  return new THREE.CatmullRomCurve3(points.map(([x, z]) => new THREE.Vector3(x, GROUND_Y, z)));
}

// One route: a glowing base line + a scrolling flow overlay + its cars.
// The trails (glow + flow) are a FLY-IN treatment only — they fade out once the
// camera settles at street level; the cars stay as ongoing city life.
function Route({ route, tex, selected, showTrails }) {
  const [pts, setPts] = useState(route.points);
  const setRouteEdit = useWorldStore((s) => s.setRouteEdit);
  const selPt = useWorldStore((s) => s.routeEdit.ptIdx);
  const carScale = useWorldStore((s) => s.carScale); // town-scale fit (AdminPanel · cars)
  // Real car models when the fleet has any, blocks when it does not. The shift
  // is module-wide so every route draws from the same rotation — otherwise each
  // route would start at its own car and the same model would cluster.
  //
  // Loads IMMEDIATELY on mount (2026-08-02: the gate carries the weight now —
  // decode behind the loading overlay, not during the fly-in). The staged
  // one-at-a-time reveal still applies; it just runs while nobody is watching.
  // Previous rule, kept for the history:
  // streams in one model at a time once the camera has arrived. See carFleet.
  const { cars: fleet, revealed } = useCarFleet(true);
  const fleetShift = FLEET_SHIFT;
  // Which model this slot will BE, decided from the full manifest so it never
  // changes as the fleet fills in. Null until that model is revealed.
  const carFor = (i) => {
    if (!fleet.length) return null;
    const idx = (i + fleetShift) % fleet.length;
    return idx < revealed ? fleet[idx] : null;
  };
  const EDIT = useWorldStore((s) => s.adminTool) === 'routes';
  const baseMatRef = useRef();
  const flowMatRef = useRef();
  const trailRef = useRef();
  const fade = useRef(1);
  const carsRef = useRef([]);

  // Load a saved (edited) path if present.
  useEffect(() => {
    try { const s = JSON.parse(localStorage.getItem(`route:${route.id}`) || 'null'); if (s?.length) setPts(s); } catch { /* noop */ }
  }, [route.id]);

  const curve = useMemo(() => curveFor(pts), [pts]);
  const len = useMemo(() => curve.getLength(), [curve]);
  const geom = useMemo(() => new THREE.TubeGeometry(curve, Math.max(24, pts.length * 24), 0.28, 8, false), [curve, pts.length]);
  useEffect(() => () => geom.dispose(), [geom]);

  // Tile the flow texture by length so the "comets" keep a constant size.
  const flowTex = useMemo(() => {
    const t = tex.clone(); t.needsUpdate = true; t.repeat.set(Math.max(2, len / 14), 1); return t;
  }, [tex, len]);

  // Fewer cars on a phone. The route counts were tuned when a car was a 4-draw
  // block; a real car is several times that, so the same nine cars are a much
  // bigger bill than they were. Thinning the traffic keeps the street alive
  // instead of switching it off — the adaptive tier, not a feature cut.
  const carCount = IS_MOBILE ? Math.max(1, Math.round((route.cars || 0) / 2)) : (route.cars || 0);
  const cars = useMemo(() => Array.from({ length: carCount }, (_, i) => i / (carCount || 1)), [carCount]);

  useFrame((_, dt) => {
    // Fade the trails in for the fly-in, out once we're on the ground.
    fade.current += ((showTrails ? 1 : 0) - fade.current) * 0.08;
    if (trailRef.current) trailRef.current.visible = fade.current > 0.01;
    if (baseMatRef.current) baseMatRef.current.opacity = 0.28 * fade.current;
    if (flowMatRef.current) {
      flowMatRef.current.opacity = 0.9 * fade.current;
      if (flowMatRef.current.map) flowMatRef.current.map.offset.x -= dt * 0.5; // energy flows
    }
    // Advance the cars along the curve (looping), facing their tangent.
    const step = (route.speed || 8) * dt / (len || 1);
    carsRef.current.forEach((g, i) => {
      if (!g) return;
      g.userData.t = ((g.userData.t ?? cars[i]) + step) % 1;
      const t = g.userData.t;
      const p = curve.getPointAt(t);
      const tan = curve.getTangentAt(t);
      // Ride height tracks car scale so the body rests on the road at any size
      // (box is centre-origin, 0.8 tall → half-height 0.4·scale above ground).
      g.position.set(p.x, GROUND_Y + 0.4 * carScale, p.z);
      g.rotation.y = Math.atan2(tan.x, tan.z);
    });
  });

  // Edit mode: click a node to select; , / . cycle; arrows move it on X/Z;
  // n inserts a node after the selected one (so a route can snake through the
  // city); x deletes it. Every change logs + saves the paste-ready line.
  useEffect(() => {
    if (!EDIT || !selected) return undefined;
    const round = (v) => Math.round(v * 10) / 10;
    const persist = (next) => {
      localStorage.setItem(`route:${route.id}`, JSON.stringify(next));
      console.log(`{ id: '${route.id}', color: '${route.color}', points: ${JSON.stringify(next)}, cars: ${route.cars}, speed: ${route.speed} },`);
    };
    const onKey = (e) => {
      const cur = useWorldStore.getState().routeEdit.ptIdx;
      if (e.key === ',') { setRouteEdit({ ptIdx: (cur - 1 + pts.length) % pts.length }); return; }
      if (e.key === '.') { setRouteEdit({ ptIdx: (cur + 1) % pts.length }); return; }
      if (e.key === 'n' || e.key === '+') {
        e.preventDefault();
        const a = pts[cur]; const b = pts[cur + 1];
        const np = b
          ? [round((a[0] + b[0]) / 2), round((a[1] + b[1]) / 2)]                                   // midpoint to next
          : [round(a[0] + (a[0] - (pts[cur - 1]?.[0] ?? a[0] - 4))), round(a[1] + (a[1] - (pts[cur - 1]?.[1] ?? a[1])))]; // extend the tail
        const next = [...pts]; next.splice(cur + 1, 0, np);
        setPts(next); persist(next); setRouteEdit({ ptIdx: cur + 1 });
        return;
      }
      if (e.key === 'x' || e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (pts.length <= 2) return; // a route needs at least a segment
        const next = pts.filter((_, i) => i !== cur);
        setPts(next); persist(next); setRouteEdit({ ptIdx: Math.min(cur, next.length - 1) });
        return;
      }
      const step = e.shiftKey ? 0.5 : 3;
      let dx = 0, dz = 0;
      if (e.key === 'ArrowLeft') dx = -step; else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dz = -step; else if (e.key === 'ArrowDown') dz = step;
      else return;
      e.preventDefault();
      const next = pts.map((p, i) => (i === cur ? [round(p[0] + dx), round(p[1] + dz)] : p));
      setPts(next); persist(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [EDIT, selected, pts, route, setRouteEdit]);

  return (
    <group>
      {/* trails — fly-in only (faded by `fade` in useFrame) */}
      <group ref={trailRef}>
        {/* soft base glow of the route */}
        <mesh geometry={geom}>
          <meshBasicMaterial ref={baseMatRef} color={route.color} transparent opacity={0.28} toneMapped={false} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* flowing energy overlay */}
        <mesh geometry={geom}>
          <meshBasicMaterial ref={flowMatRef} map={flowTex} color={route.color} transparent opacity={0.9} toneMapped={false} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
      </group>
      {/* cars */}
      {cars.map((t0, i) => {
       const car = carFor(i);
       return (
        <group key={i} ref={(g) => { carsRef.current[i] = g; if (g && g.userData.t == null) g.userData.t = t0; }}>
         <group scale={carScale} position={car ? [0, -GROUND_Y / carScale, 0] : undefined}>
          {car ? (
            // A real car once this slot's model has arrived. Suspense per car, not
            // around the route: one model still loading must not blank the traffic.
            //
            // DROPPED BY GROUND_Y, because the two kinds of car measure from
            // different places. The blocks are CENTRED on their origin, so riding
            // the wayline curve sank half a block and their underside landed on
            // the road by luck of the numbers. A real car is grounded at its own
            // lowest point (CarModel), so riding the same curve parks it a wayline
            // height in the air — about a foot and a half at carScale 0.42.
            // Divided by carScale because this offset is inside the scaled group.
            // SafeMount: a car whose GLB fails to load (a dropped connection, a
            // flooded dev proxy) used to throw up to the Canvas and take the
            // WHOLE world down — "WebGLRenderer: Context Lost" over one Elise.
            // Now that lane simply has no car, and it retries.
            <SafeMount label={`car ${car.url}`} retryMs={20000} onRetry={() => useGLTF.clear(car.url)}>
              <Suspense fallback={null}>
                <CarModel {...car} />
              </Suspense>
            </SafeMount>
          ) : (
            // Fallback: the original blocks. Kept deliberately — an empty
            // /models/cars folder should still give a city with traffic in it,
            // not empty roads.
            <>
              <mesh position={[0, 0, 0]}>
                <boxGeometry args={[1.7, 0.8, 3.8]} />
                <meshStandardMaterial color="#15151c" roughness={0.4} metalness={0.5} />
              </mesh>
              <mesh position={[0, 0.45, -0.2]}>
                <boxGeometry args={[1.5, 0.5, 1.9]} />
                <meshStandardMaterial color="#0b0b10" roughness={0.2} metalness={0.6} emissive={route.color} emissiveIntensity={0.15} />
              </mesh>
              <mesh position={[0, 0, 1.95]}><boxGeometry args={[1.5, 0.35, 0.08]} /><meshBasicMaterial color="#fff7e6" toneMapped={false} /></mesh>
              <mesh position={[0, 0, -1.95]}><boxGeometry args={[1.5, 0.3, 0.08]} /><meshBasicMaterial color="#ff2a2a" toneMapped={false} /></mesh>
            </>
          )}
         </group>
        </group>
       );
      })}
      {/* edit handles — click a node to select it (scales past keyboard cycling) */}
      {EDIT && selected && pts.map((p, i) => (
        <mesh key={i} position={[p[0], GROUND_Y + 0.6, p[1]]}
          onClick={(e) => { e.stopPropagation(); setRouteEdit({ ptIdx: i }); }}
          onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
          onPointerOut={() => { document.body.style.cursor = ''; }}
        >
          <sphereGeometry args={[i === selPt ? 1.1 : 0.7, 12, 12]} />
          <meshBasicMaterial color={i === selPt ? '#ffffff' : route.color} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// CityLife — animated neon route waylines + cars threading the streets. Gives
// the overworld motion (best seen from the drone descent / overhead). Trace the
// routes onto the real streets with ?routes=edit; see cityRoutes.js.
export default function CityLife() {
  const tex = useMemo(flowTexture, []);
  useEffect(() => () => tex.dispose(), [tex]);
  // Glowing lane trails are a BUILDING aid (route tuning) + optional wayfinding —
  // not part of the show. Off by default (including the fly-in); on only in the
  // routes editor. The `fade` in Route still eases them in/out smoothly.
  const routeIdx = useWorldStore((s) => s.routeEdit.routeIdx);
  const setRouteEdit = useWorldStore((s) => s.setRouteEdit);
  const EDIT = useWorldStore((s) => s.adminTool) === 'routes';
  const showTrails = EDIT;

  useEffect(() => {
    if (!EDIT) return undefined;
    const onKey = (e) => { const n = parseInt(e.key, 10); if (!isNaN(n) && n >= 1 && n <= CITY_ROUTES.length) setRouteEdit({ routeIdx: n - 1, ptIdx: 0 }); };
    window.addEventListener('keydown', onKey);
    console.log('[routes] edit — 1..N route · click a node to select · n add · x delete · , . cycle · arrows move (Shift=fine)');
    return () => window.removeEventListener('keydown', onKey);
  }, [EDIT, setRouteEdit]);

  // Perf test (config.VEHICLES_OFF): no traffic — unless the routes editor is
  // open, which needs the routes drawn to edit them. After the hooks on
  // purpose: an early return above a hook is a crash, not an optimisation.
  if (VEHICLES_OFF && !EDIT) return null;

  return (
    <group>
      {CITY_ROUTES.map((route, i) => (
        <Route key={route.id} route={route} tex={tex} selected={EDIT && routeIdx === i} showTrails={showTrails} />
      ))}
    </group>
  );
}
